import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchNotifications, 
  fetchUnreadCount, 
  markNotificationAsRead,
  markAllNotificationsAsRead 
} from '../../../redux/slices/notificationsSlice';
import { ArrowLeft2, ArrowRight2, Filter } from 'iconsax-reactjs';
import { useNavigate } from 'react-router-dom';
import './Notifications.css';

// Helper function to format date
const formatNotificationDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
  });
};

// Helper to get type badge color
const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'general': return 'badge-general';
    case 'news': return 'badge-news';
    case 'class': return 'badge-class';
    case 'assignment': return 'badge-assignment';
    case 'payment': return 'badge-payment';
    case 'broadcast': return 'badge-broadcast';
    default: return 'badge-general';
  }
};

// Helper to get type label
const getTypeLabel = (type) => {
  switch (type) {
    case 'general': return 'General';
    case 'news': return 'News';
    case 'class': return 'Class';
    case 'assignment': return 'Assignment';
    case 'payment': return 'Payment';
    case 'broadcast': return 'Broadcast';
    default: return type;
  }
};

function Notifications() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notifications, loading, unreadCount, pagination } = useSelector(
    (state) => state.notifications
  );
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchUnreadCount());
    loadNotifications();
  }, [dispatch]);

  useEffect(() => {
    loadNotifications();
  }, [dispatch, filterType, currentPage]);

  const loadNotifications = () => {
    const params = { per_page: 20 };
    if (filterType !== 'all') {
      params.type = filterType;
    }
    params.page = currentPage;
    dispatch(fetchNotifications(params));
  };

  const handleMarkAsRead = async (notificationId) => {
    await dispatch(markNotificationAsRead(notificationId));
    dispatch(fetchUnreadCount());
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllNotificationsAsRead());
    loadNotifications();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'general', label: 'General' },
    { value: 'news', label: 'News' },
    { value: 'class', label: 'Class' },
    { value: 'assignment', label: 'Assignment' },
    { value: 'payment', label: 'Payment' },
    { value: 'broadcast', label: 'Broadcast' },
  ];

  return (
    <div className="notifications-page">
      <div className="notifications-page-header">
        <div className="d-flex align-items-center gap-3">
          <button onClick={() => navigate(-1)} className="back-btn" title="Go back">
            <ArrowLeft2 size="20" />
          </button>
          <div>
            <h2>Notifications</h2>
            <p className="text-muted mb-0">
              You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {unreadCount > 0 && (
          <button 
            className="mark-all-read-btn"
            onClick={handleMarkAllAsRead}
            disabled={loading}
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="notifications-filter">
        <Filter size="18" />
        <span>Filter by:</span>
        <div className="filter-buttons">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              className={`filter-btn ${filterType === option.value ? 'active' : ''}`}
              onClick={() => {
                setFilterType(option.value);
                setCurrentPage(1);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading && notifications.length === 0 ? (
        <div className="notifications-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">
          <div className="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <h4>No notifications yet</h4>
          <p>When you receive notifications, they'll show up here</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`notification-card ${notification.is_read ? 'read' : 'unread'}`}
              onClick={() => handleMarkAsRead(notification.id)}
            >
              {!notification.is_read && <span className="unread-indicator"></span>}
              
              <div className="notification-card-header">
                <span className={`notification-type-badge ${getTypeBadgeClass(notification.type)}`}>
                  {getTypeLabel(notification.type)}
                </span>
                <span className="notification-time">
                  {formatNotificationDate(notification.created_at)}
                </span>
              </div>
              
              <h4 className="notification-title">{notification.title}</h4>
              <div 
                className="notification-content"
                dangerouslySetInnerHTML={{ __html: notification.content }}
              />
              
              {notification.cohort && (
                <div className="notification-cohort">
                  <span className="cohort-label">Cohort:</span>
                  <span className="cohort-name">{notification.cohort.name}</span>
                </div>
              )}
              
              {notification.image_url && (
                <div className="notification-image">
                  <img src={notification.image_url} alt="" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pagination.lastPage > 1 && (
        <div className="notifications-pagination">
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ArrowLeft2 size="16" />
            Previous
          </button>
          
          <div className="pagination-pages">
            {Array.from({ length: pagination.lastPage }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.lastPage}
          >
            Next
            <ArrowRight2 size="16" />
          </button>
        </div>
      )}
    </div>
  );
}

export default Notifications;
